/** Business Files — v1 market-backfill.js BIZFILES, verbatim contract:
 *   GET  /api/auth/status                  → { connected: bool }
 *   GET  /api/drive/business-files         → { folderId, items:[{id,name,mimeType,webViewLink}] }
 *   GET  /api/drive/business-files?folder_id=X → same, for a subfolder
 *   401 → the Drive session expired: connected=false + v1's reconnect toast.
 * Folders are mimeType 'application/vnd.google-apps.folder'; files open their
 * webViewLink in Drive. Search filters the CURRENT folder client-side (v1
 * bizSet). Mutations (Connect OAuth, Disconnect, New Folder, Upload) are
 * Phase B — buttons render disabled with the note. */
import { backendFetch, BackendError } from "../../lib/backend";

export interface BizItem {
  id: string;
  name: string;
  kind: "folder" | "file";
  link: string | null;
}

export interface BizFolder {
  folderId: string | null;
  items: BizItem[];
}

export interface BizCrumb {
  id: string;
  name: string;
}

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** v1: type = mimeType==='application/vnd.google-apps.folder' ? folder : file */
export function narrowBizItem(raw: unknown): BizItem | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = s(o["id"]);
  const name = s(o["name"]);
  if (!id || !name) return null;
  return {
    id,
    name,
    kind: o["mimeType"] === "application/vnd.google-apps.folder" ? "folder" : "file",
    link: s(o["webViewLink"]),
  };
}

/** v1 bizSet: case-insensitive name filter over the current folder only. */
export function bizFilter(items: BizItem[], q: string): BizItem[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((it) => it.name.toLowerCase().includes(needle));
}

/** v1 bizCrumbTo: -1 = root, i = keep path[0..i]. Returns the new path and
 * the folder id to load (null = root). */
export function crumbTo(path: BizCrumb[], i: number): { path: BizCrumb[]; folderId: string | null } {
  if (i < 0) return { path: [], folderId: null };
  const next = path.slice(0, i + 1);
  const last = next[next.length - 1];
  return { path: next, folderId: last ? last.id : null };
}

export type BizStatus = "connected" | "disconnected" | "transient";

/** GET /api/auth/status — v1 driveCheckStatus (any error → not connected). */
export async function fetchBizStatus(token: string): Promise<boolean> {
  try {
    const d = await backendFetch<{ connected?: unknown }>(token, "/api/auth/status", { timeoutMs: 30000 });
    return d?.connected === true;
  } catch {
    return false;
  }
}

/** GET /api/drive/business-files[?folder_id=] — 401 = expired (v1 semantics),
 * other failures are transient (Render wake handled inside backendFetch). */
export async function fetchBizFolder(
  token: string,
  folderId: string | null,
  onWake?: () => void,
): Promise<{ status: BizStatus; folder: BizFolder }> {
  const path = "/api/drive/business-files" + (folderId ? "?folder_id=" + encodeURIComponent(folderId) : "");
  try {
    const d = await backendFetch<{ folderId?: unknown; items?: unknown }>(
      token,
      path,
      onWake ? { timeoutMs: 45000, onWake } : { timeoutMs: 45000 },
    );
    const items = Array.isArray(d?.items) ? d.items.map(narrowBizItem).filter((x): x is BizItem => x !== null) : [];
    return { status: "connected", folder: { folderId: s(d?.folderId), items } };
  } catch (e) {
    if (e instanceof BackendError && e.status === 401) {
      return { status: "disconnected", folder: { folderId: null, items: [] } };
    }
    return { status: "transient", folder: { folderId: null, items: [] } };
  }
}

export const BIZ_MSG = {
  title: "Business Files",
  connected: "Connected to Google Drive",
  notConnected: "Not connected",
  expired: "Google Drive session expired — reconnect from v1 (Business Files → Connect Google Drive) until the live-fire session brings the flow here.",
  transient: "Drive didn't answer (the backend may be waking) — try Refresh again in a few seconds.",
  empty: "This folder is empty.",
  phaseB: "Connect / uploads / new folders go live in the sit-down session (Phase B) — browsing is read-only until then.",
  note: "Connects to the Business Files folder in Google Drive. Live sync uses the Drive backend (OAuth) — see backend/README.md. Until deployed, this works in local mode.",
  folderSub: "Folder — open",
  fileSub: "File — open in Drive",
};
